/**
 * Comprehensive Phone Number Validation and Formatting Utility
 * Ensures AWS SNS compatibility and provides user-friendly error messages
 */

// AWS SNS Phone Number Requirements:
// - Must be in E.164 format: +[country code][number]
// - US numbers: +1XXXXXXXXXX (11 digits total)
// - Valid area codes and exchange codes
// - No special characters except + at the beginning

// Valid US area codes (simplified list - in production, use a complete list)
const VALID_AREA_CODES = [
    '201', '202', '203', '205', '206', '207', '208', '209', '210', '212', '213', '214', '215', '216', '217', '218', '219', '220', '224', '225', '228', '229', '231', '234', '239', '240', '248', '251', '252', '253', '254', '256', '260', '262', '267', '269', '270', '272', '276', '281', '301', '302', '303', '304', '305', '307', '308', '309', '310', '312', '313', '314', '315', '316', '317', '318', '319', '320', '321', '323', '325', '330', '331', '334', '336', '337', '339', '346', '347', '351', '352', '360', '361', '364', '369', '380', '385', '386', '401', '402', '404', '405', '406', '407', '408', '409', '410', '412', '413', '414', '415', '417', '419', '423', '424', '425', '430', '432', '434', '435', '440', '442', '443', '447', '458', '463', '464', '469', '470', '475', '478', '479', '480', '484', '501', '502', '503', '504', '505', '507', '508', '509', '510', '512', '513', '515', '516', '517', '518', '520', '530', '531', '534', '539', '540', '541', '551', '559', '561', '562', '563', '564', '567', '570', '571', '573', '574', '575', '580', '585', '586', '601', '602', '603', '605', '606', '607', '608', '609', '610', '612', '614', '615', '616', '617', '618', '619', '620', '623', '626', '628', '629', '630', '631', '636', '641', '646', '650', '651', '657', '660', '661', '662', '667', '669', '678', '681', '682', '689', '701', '702', '703', '704', '706', '707', '708', '712', '713', '714', '715', '716', '717', '718', '719', '720', '724', '725', '727', '731', '732', '734', '737', '740', '743', '747', '754', '757', '760', '762', '763', '765', '769', '770', '772', '773', '774', '775', '779', '781', '785', '786', '787', '801', '802', '803', '804', '805', '806', '808', '810', '812', '813', '814', '815', '816', '817', '818', '828', '830', '831', '832', '843', '845', '847', '848', '850', '856', '857', '858', '859', '860', '862', '863', '864', '865', '870', '872', '878', '901', '903', '904', '906', '907', '908', '909', '910', '912', '913', '914', '915', '916', '917', '918', '919', '920', '925', '928', '929', '930', '931', '934', '936', '937', '938', '940', '941', '947', '949', '951', '952', '954', '956', '959', '970', '971', '972', '973', '978', '979', '980', '984', '985', '989'
];

// Invalid exchange codes (NPA-NXX patterns that don't exist)
const INVALID_EXCHANGE_PATTERNS = [
    /^[2-9]11$/, // N11 codes (except 911 which is valid)
    /^[2-9]00$/, // N00 codes
    /^[2-9]01$/, // N01 codes
    /^[2-9]10$/  // N10 codes
];

/**
 * Validate and format phone number for AWS SNS compatibility
 */
export const validateAndFormatPhoneNumber = (input) => {
    const result = {
        isValid: false,
        formattedNumber: '',
        errorMessage: '',
        suggestions: [],
        originalInput: input
    };

    try {
        // Step 1: Clean the input
        const cleanedInput = input.trim();

        if (!cleanedInput) {
            result.errorMessage = 'Please enter a phone number';
            result.suggestions = ['Enter your 10-digit US phone number (e.g., 555-123-4567)'];
            return result;
        }

        // Step 2: Extract digits and check for valid characters
        const digits = cleanedInput.replace(/\D/g, '');
        const hasPlus = cleanedInput.includes('+');
        const hasInvalidChars = /[^\d\s\-\(\)\.\+]/.test(cleanedInput);

        // Auto-add + prefix if not present and input looks like a US number
        let processedInput = cleanedInput;
        if (!hasPlus && digits.length >= 10 && digits.length <= 11) {
            processedInput = '+' + cleanedInput;
        }

        if (hasInvalidChars) {
            result.errorMessage = 'Phone number contains invalid characters';
            result.suggestions = [
                'Only use numbers, spaces, dashes, periods, and parentheses',
                'Remove any letters or special symbols',
                'Try: 555-123-4567, (555) 123-4567, 555.123.4567, or 5551234567'
            ];
            return result;
        }

        // Step 3: Check length and format
        if (digits.length < 10) {
            result.errorMessage = 'Phone number is too short';
            result.suggestions = [
                'Enter your full 10-digit US phone number',
                'Include the area code (first 3 digits)',
                'Example: 555-123-4567'
            ];
            return result;
        }

        if (digits.length > 11) {
            result.errorMessage = 'Phone number is too long';
            result.suggestions = [
                'Enter only your 10-digit US phone number',
                'Remove any extra digits or country codes',
                'Example: 555-123-4567'
            ];
            return result;
        }

        // Step 4: Handle different input formats
        let phoneNumber = '';

        if (digits.length === 10) {
            // 10-digit number - assume US, add +1 prefix
            phoneNumber = `+1${digits}`;
        } else if (digits.length === 11 && digits.startsWith('1')) {
            // 11-digit number starting with 1 - US with country code
            phoneNumber = `+${digits}`;
        } else if (digits.length === 11 && !digits.startsWith('1')) {
            // 11-digit number not starting with 1 - invalid for US
            result.errorMessage = 'Invalid phone number format';
            result.suggestions = [
                'Enter your 10-digit US phone number without country code',
                'Remove the leading digit if it\'s not 1',
                'Example: 555-123-4567'
            ];
            return result;
        }

        // Step 5: Validate area code
        const areaCode = phoneNumber.substring(2, 5);
        if (!VALID_AREA_CODES.includes(areaCode)) {
            result.errorMessage = 'Invalid area code';
            result.suggestions = [
                'Check that your area code is correct',
                'Make sure you\'re entering a US phone number',
                'Common area codes: 212 (NYC), 310 (LA), 312 (Chicago), 415 (SF)'
            ];
            return result;
        }

        // Step 6: Validate exchange code (first 3 digits of the local number)
        const exchangeCode = phoneNumber.substring(5, 8);
        const fullExchange = areaCode + exchangeCode;

        // Check for invalid exchange patterns
        for (const pattern of INVALID_EXCHANGE_PATTERNS) {
            if (pattern.test(exchangeCode)) {
                result.errorMessage = 'Invalid phone number format';
                result.suggestions = [
                    'This exchange code is not valid',
                    'Double-check your phone number',
                    'Make sure all digits are correct'
                ];
                return result;
            }
        }

        // Step 7: Final validation for AWS SNS format
        const snsPattern = /^\+1[2-9]\d{2}[2-9]\d{6}$/;
        if (!snsPattern.test(phoneNumber)) {
            result.errorMessage = 'Phone number format not compatible with SMS service';
            result.suggestions = [
                'Make sure your phone number is a valid US mobile or landline',
                'Area code and exchange must start with digits 2-9',
                'Try entering your number as: 555-123-4567'
            ];
            return result;
        }

        // Step 8: Success
        result.isValid = true;
        result.formattedNumber = phoneNumber;
        result.errorMessage = '';
        result.suggestions = [];

        return result;

    } catch (error) {
        result.errorMessage = 'Error processing phone number';
        result.suggestions = [
            'Please try entering your number again',
            'Use format: 555-123-4567',
            'Contact support if the problem continues'
        ];
        return result;
    }
};

/**
 * Format phone number for display purposes
 */
export const formatPhoneForDisplay = (phoneNumber) => {
    if (!phoneNumber) return '';

    // Remove +1 prefix for US numbers
    const digits = phoneNumber.replace(/^\+1/, '');

    if (digits.length === 10) {
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    return phoneNumber;
};

/**
 * Auto-format phone number as user types (without requiring + sign)
 */
export const autoFormatPhoneInput = (input) => {
    // Remove all non-digit characters except + at the beginning
    const hasPlus = input.startsWith('+');
    const digits = input.replace(/\D/g, '');

    // Don't format if empty or too long
    if (!digits || digits.length > 10) {
        return input;
    }

    // If user is typing 10 contiguous digits, format them as they type
    if (digits.length === 10 && !input.includes('(') && !input.includes('-') && !input.includes('.')) {
        // Format as (XXX) XXX-XXXX
        return `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    // Format based on length for partial input
    let formatted = '';
    if (digits.length <= 3) {
        formatted = digits;
    } else if (digits.length <= 6) {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3)}`;
    } else {
        formatted = `(${digits.substring(0, 3)}) ${digits.substring(3, 6)}-${digits.substring(6)}`;
    }

    // Preserve + if it was there, but don't add it automatically
    return hasPlus ? `+${formatted}` : formatted;
};

/**
 * Get user-friendly error message with suggestions
 */
export const getPhoneValidationErrorMessage = (validationResult) => {
    if (!validationResult.errorMessage) return '';

    let message = validationResult.errorMessage;

    if (validationResult.suggestions.length > 0) {
        message += '\n\nTry this:\n• ' + validationResult.suggestions.join('\n• ');
    }

    return message;
};
